/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BeakerIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  FunnelIcon,
  PlayIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  FolderIcon,
  ChevronDownIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { experimentService, Experiment, ExperimentStats, Run } from '../../services/registryApi';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { useProjects } from '../../hooks/useProjects';
import toast from 'react-hot-toast';

const STATUS_COLORS: Record<string, string> = {
  running: '#3B82F6',
  completed: '#10B981',
  failed: '#EF4444',
  killed: '#6B7280',
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
    active: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircleIcon },
    archived: { bg: 'bg-gray-700', text: 'text-gray-300', icon: ArchiveBoxIcon },
  };

  const { bg, text, icon: Icon } = config[status] || config.active;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const RunStatusBadge: React.FC<{ status: Run['status'] }> = ({ status }) => {
  const config = {
    running: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: PlayIcon },
    completed: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircleIcon },
    failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircleIcon },
    killed: { bg: 'bg-gray-700', text: 'text-gray-300', icon: StopCircleIcon },
  };

  const { bg, text, icon: Icon } = config[status] || config.running;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${bg} ${text}`}>
      <Icon className="w-3 h-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
  subtitle?: string;
}> = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const formatDuration = (ms: number): string => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${seconds}s`;
};

// Archive Confirmation Modal
const ArchiveConfirmModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  experimentName: string;
  loading: boolean;
}> = ({ isOpen, onClose, onConfirm, experimentName, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-500/20 rounded-full">
            <ArchiveBoxIcon className="w-6 h-6 text-amber-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">
            Archive Experiment
          </h2>
        </div>

        <p className="text-gray-300 mb-6">
          Are you sure you want to archive the experiment "<span className="font-medium">{experimentName}</span>"?
          Archived experiments can be viewed but not modified.
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Archiving...' : 'Archive'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExperimentTracking: React.FC = () => {
  const navigate = useNavigate();
  const {
    selectedProjectId,
    setSelectedProject,
    autoSelectFirstProject,
    projects,
    projectsLoading,
  } = useSelectedProject();
  const { createProject, isCreating: isCreatingProject } = useProjects();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [stats, setStats] = useState<ExperimentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newExperiment, setNewExperiment] = useState({ name: '', description: '', projectId: '' });
  const [creating, setCreating] = useState(false);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });

  // Archive modal state
  const [archiveModal, setArchiveModal] = useState<{
    isOpen: boolean;
    experiment: Experiment | null;
  }>({ isOpen: false, experiment: null });
  const [archiving, setArchiving] = useState(false);

  // Auto-select first project when projects load and none is selected
  useEffect(() => {
    if (!projectsLoading) autoSelectFirstProject();
  }, [projectsLoading, autoSelectFirstProject]);

  // Keep create-modal project in sync with the globally selected project
  useEffect(() => {
    setNewExperiment(prev => ({ ...prev, projectId: selectedProjectId }));
  }, [selectedProjectId]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    localStorage.setItem('selectedProjectId', projectId);
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await experimentService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchExperiments = async () => {
    try {
      setLoading(true);
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const response = await experimentService.list({ status, limit: 100 });
      setExperiments(response.data.experiments || []);
    } catch (error) {
      console.error('Failed to fetch experiments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProjectId) fetchStats();
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId) fetchExperiments();
  }, [statusFilter, selectedProjectId]);

  const handleCreate = async () => {
    if (!newExperiment.name.trim()) {
      toast.error('Experiment name is required');
      return;
    }
    if (!newExperiment.projectId) {
      toast.error('Please select a project');
      return;
    }

    try {
      setCreating(true);
      const response = await experimentService.create({
        name: newExperiment.name,
        description: newExperiment.description,
        project_id: newExperiment.projectId,
      });
      toast.success('Experiment created successfully');

      // If user picked a different project in the modal, persist that choice globally
      if (newExperiment.projectId && newExperiment.projectId !== selectedProjectId) {
        setSelectedProject(newExperiment.projectId);
        localStorage.setItem('selectedProjectId', newExperiment.projectId);
      }

      setShowCreateModal(false);
      setNewExperiment({ name: '', description: '', projectId: newExperiment.projectId });
      setShowNewProjectForm(false);
      setNewProject({ name: '', description: '' });
      fetchExperiments();
      fetchStats();
    } catch (error) {
      console.error('Failed to create experiment:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateProject = () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    createProject(
      { name: newProject.name, description: newProject.description },
      {
        onSuccess: (response: any) => {
          const createdProject = response?.data?.project;
          if (createdProject?.id) {
            setNewExperiment(prev => ({ ...prev, projectId: createdProject.id }));
          }
          setShowNewProjectForm(false);
          setNewProject({ name: '', description: '' });
        },
      }
    );
  };

  const openArchiveModal = (exp: Experiment) => {
    setArchiveModal({ isOpen: true, experiment: exp });
  };

  const closeArchiveModal = () => {
    setArchiveModal({ isOpen: false, experiment: null });
  };

  const handleConfirmArchive = async () => {
    if (!archiveModal.experiment) return;

    try {
      setArchiving(true);
      await experimentService.archive(archiveModal.experiment.id);
      toast.success('Experiment archived');
      fetchExperiments();
      fetchStats();
      closeArchiveModal();
    } catch (error) {
      console.error('Failed to archive experiment:', error);
    } finally {
      setArchiving(false);
    }
  };

  const filteredExperiments = experiments.filter((exp) =>
    exp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    exp.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Prepare runs by status chart data
  const runsByStatusData = useMemo(() => {
    if (!stats?.runs_by_status) return [];
    return Object.entries(stats.runs_by_status).map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: STATUS_COLORS[status] || '#6B7280',
    }));
  }, [stats]);

  // Calculate success rate
  const successRate = useMemo(() => {
    if (!stats?.runs_by_status || stats.total_runs === 0) return 0;
    const completed = stats.runs_by_status.completed || 0;
    return Math.round((completed / stats.total_runs) * 100);
  }, [stats]);

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Experiment Tracking</h1>
            <p className="mt-1 text-sm text-gray-400">
              Track and manage your ML experiments and training runs
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Project Selector */}
            <div className="relative">
              <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={projectsLoading}
                className="pl-9 pr-8 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-primary-500 appearance-none cursor-pointer disabled:opacity-50"
              >
                {projects.length === 0 && <option value="">No projects</option>}
                {projects.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              New Experiment
            </button>
          </div>
        </div>

        {/* Stats Dashboard */}
        {!statsLoading && stats && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Total Experiments"
                value={stats.total_experiments}
                icon={BeakerIcon}
                color="bg-primary-500"
              />
              <StatCard
                title="Total Runs"
                value={stats.total_runs}
                icon={PlayIcon}
                color="bg-green-500"
              />
              <StatCard
                title="Completed Runs"
                value={stats.runs_by_status?.completed || 0}
                icon={CheckCircleIcon}
                color="bg-emerald-500"
                subtitle={`${successRate}% success rate`}
              />
              <StatCard
                title="Failed Runs"
                value={stats.runs_by_status?.failed || 0}
                icon={XCircleIcon}
                color="bg-red-500"
              />
            </div>

            {/* Charts Row */}
            {stats.total_runs > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Runs Over Time */}
                <div className="lg:col-span-2 bg-gray-900 rounded-xl border border-gray-700 p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4">Runs Over Time (Last 30 Days)</h3>
                  {stats.runs_over_time && stats.runs_over_time.length > 0 ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={stats.runs_over_time}>
                          <defs>
                            <linearGradient id="colorRuns" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#F97316" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#F97316" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3d" />
                          <XAxis
                            dataKey="date"
                            stroke="#9CA3AF"
                            tick={{ fill: '#9CA3AF', fontSize: 11 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getMonth() + 1}/${date.getDate()}`;
                            }}
                          />
                          <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: '#1a1a28',
                              border: 'none',
                              borderRadius: '8px',
                              color: '#fff',
                            }}
                            labelFormatter={(value) => new Date(value).toLocaleDateString()}
                          />
                          <Area
                            type="monotone"
                            dataKey="count"
                            stroke="#F97316"
                            strokeWidth={2}
                            fillOpacity={1}
                            fill="url(#colorRuns)"
                            name="Runs"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-gray-400">
                      No run data for the last 30 days
                    </div>
                  )}
                </div>

                {/* Runs by Status */}
                <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
                  <h3 className="text-sm font-medium text-gray-300 mb-4">Runs by Status</h3>
                  {runsByStatusData.length > 0 ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={runsByStatusData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={60}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {runsByStatusData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-gray-400">
                      No run data yet
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recent Runs */}
            {stats.recent_runs && stats.recent_runs.length > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-4 mb-6">
                <h3 className="text-sm font-medium text-gray-300 mb-4">Recent Runs</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="text-left text-xs font-medium text-gray-400 uppercase">
                        <th className="px-4 py-2">Run</th>
                        <th className="px-4 py-2">Status</th>
                        <th className="px-4 py-2">Duration</th>
                        <th className="px-4 py-2">Started</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {stats.recent_runs.slice(0, 5).map((run) => (
                        <tr
                          key={run.id}
                          onClick={() => navigate(`/registry/runs/${run.id}`)}
                          className="hover:bg-gray-800 cursor-pointer"
                        >
                          <td className="px-4 py-3">
                            <span className="text-sm font-medium text-white">
                              {run.name || run.id.slice(0, 8)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <RunStatusBadge status={run.status} />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {formatDuration(run.duration_ms)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {run.start_time ? new Date(run.start_time * 1000).toLocaleString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search experiments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
              className="px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {/* Experiments Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-gray-700 border-t-primary-500"></div>
          </div>
        ) : filteredExperiments.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-700">
            <BeakerIcon className="mx-auto h-12 w-12 text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-white">No experiments</h3>
            <p className="mt-1 text-sm text-gray-400">
              Get started by creating a new experiment to track your ML runs.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Create Experiment
            </button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredExperiments.map((exp) => (
              <div
                key={exp.id}
                onClick={() => navigate(`/registry/experiments/${exp.id}`)}
                className="bg-gray-900 rounded-xl border border-gray-700 p-6 hover:shadow-lg hover:border-primary-500 transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <BeakerIcon className="w-5 h-5 text-primary-500 flex-shrink-0" />
                      <h3 className="text-lg font-semibold text-white truncate">
                        {exp.name}
                      </h3>
                    </div>
                    {exp.description && (
                      <p className="mt-1 text-sm text-gray-400 line-clamp-2">
                        {exp.description}
                      </p>
                    )}
                  </div>
                  {exp.status === 'active' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openArchiveModal(exp);
                      }}
                      className="p-1 text-gray-600 hover:text-gray-300 rounded"
                      title="Archive experiment"
                    >
                      <ArchiveBoxIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="mt-4 flex items-center gap-3">
                  <StatusBadge status={exp.status} />
                </div>

                {exp.tags && Object.keys(exp.tags).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {Object.entries(exp.tags).slice(0, 3).map(([key, value]) => (
                      <span
                        key={key}
                        className="px-2 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded"
                      >
                        {key}: {value}
                      </span>
                    ))}
                    {Object.keys(exp.tags).length > 3 && (
                      <span className="text-xs text-gray-400">+{Object.keys(exp.tags).length - 3}</span>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center gap-1 text-sm text-gray-400">
                    <PlayIcon className="w-4 h-4" />
                    <span>{exp.run_count} runs</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(exp.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4">
                Create New Experiment
              </h2>
              <div className="space-y-4">
                {/* Project Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Project *
                  </label>
                  {!showNewProjectForm ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <select
                            value={newExperiment.projectId}
                            onChange={(e) => setNewExperiment({ ...newExperiment, projectId: e.target.value })}
                            className="w-full pl-10 pr-10 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 appearance-none"
                            disabled={projectsLoading}
                          >
                            <option value="">Select a project</option>
                            {projects.map((project: any) => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowNewProjectForm(true)}
                          className="px-3 py-2 text-sm font-medium text-primary-500 hover:text-primary-400 border border-primary-500 hover:border-primary-400 rounded-lg transition-colors whitespace-nowrap"
                        >
                          <PlusIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3 p-3 border border-gray-700 rounded-lg bg-gray-800">
                      <p className="text-sm font-medium text-gray-300">Create New Project</p>
                      <input
                        type="text"
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-primary-500 text-sm"
                        placeholder="Project name"
                      />
                      <input
                        type="text"
                        value={newProject.description}
                        onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-primary-500 text-sm"
                        placeholder="Project description (optional)"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowNewProjectForm(false);
                            setNewProject({ name: '', description: '' });
                          }}
                          className="px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-700 rounded transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateProject}
                          disabled={isCreatingProject || !newProject.name.trim()}
                          className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                          {isCreatingProject ? 'Creating...' : 'Create Project'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Experiment Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Experiment Name *
                  </label>
                  <input
                    type="text"
                    value={newExperiment.name}
                    onChange={(e) => setNewExperiment({ ...newExperiment, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="my-experiment"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newExperiment.description}
                    onChange={(e) => setNewExperiment({ ...newExperiment, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    placeholder="Experiment description..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowNewProjectForm(false);
                    setNewProject({ name: '', description: '' });
                  }}
                  className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || showNewProjectForm}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Experiment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Archive Confirmation Modal */}
        <ArchiveConfirmModal
          isOpen={archiveModal.isOpen}
          onClose={closeArchiveModal}
          onConfirm={handleConfirmArchive}
          experimentName={archiveModal.experiment?.name || ''}
          loading={archiving}
        />
      </div>
    </div>
  );
};

export default ExperimentTracking;
